import type { Ref } from 'vue';
import {
  computed,
  ref,
} from 'vue';

import type {
  IPaymentSystemFieldNames,
  IPaymentSystemFormSchema,
  IPaymentSystemQueryType,
  IPaymentSystemsFields,
} from '@modules/payment-systems/types';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import {
  PAYMENT_SYSTEMS,
  TRANSLATION_GROUP_PAYMENT_SYSTEMS,
} from '@modules/payment-systems/constants';
import { usePaymentSystemsCountriesQuery } from '@modules/payment-systems/queries';

export interface PaymentSystemsComposable {
  paymentSystemSchema: Ref<IPaymentSystemFormSchema>;
  isLoading: Ref<boolean>;
}

export default function usePaymentSystems(
  paymentSystem: Ref<string | undefined>,
  affId?: Ref<number | undefined>,
  withDefault: Ref<boolean> = ref(false),
  queryType: Ref<IPaymentSystemQueryType> = ref('default'),
  isQueryDisabled = false,
  values?: Ref<IPaymentSystemsFields>,
): PaymentSystemsComposable {
  const { getTranslation } = useI18n();

  const affiliateId = computed(() => (
    affId ? affId.value : 0
  ));

  const { data: countries, isLoading: isLoadingCountries } = usePaymentSystemsCountriesQuery(
    affiliateId,
    queryType,
    isQueryDisabled,
  );

  const isLoading = computed(() => !isQueryDisabled && isLoadingCountries.value);

  const fullPaymentSystemSchema = computed<IPaymentSystemFormSchema>(() => ({
    country_code: {
      type: EFormInputTypes.SELECT,
      options: countries.value ?? [],
      optionValue: 'country_code',
      optionLabel: 'name',
      value: values?.value.country_code,
    },
    account_holders_name: {
      type: EFormInputTypes.TEXT,
      value: values?.value.account_holders_name,
    },
    branch_code: {
      type: EFormInputTypes.TEXT,
      value: values?.value.branch_code,
    },
    routing_number: {
      type: EFormInputTypes.TEXT,
      value: values?.value.routing_number,
    },
    username: {
      type: EFormInputTypes.TEXT,
      value: values?.value.username,
    },
    poker_nickname: {
      type: EFormInputTypes.TEXT,
      value: values?.value.poker_nickname,
    },
    email: {
      type: EFormInputTypes.TEXT,
      value: values?.value.email,
    },
    wallet: {
      type: EFormInputTypes.TEXT,
      value: values?.value.wallet,
    },
    card_number: {
      type: EFormInputTypes.TEXT,
      value: values?.value.card_number,
    },
    country: {
      type: EFormInputTypes.TEXT,
      value: values?.value.country,
    },
    address: {
      type: EFormInputTypes.TEXT,
      value: values?.value.address,
    },
    city: {
      type: EFormInputTypes.TEXT,
      value: values?.value.city,
    },
    company_name: {
      type: EFormInputTypes.TEXT,
      value: values?.value.company_name,
    },
    company_country: {
      type: EFormInputTypes.TEXT,
      value: values?.value.company_country,
    },
    company_city: {
      type: EFormInputTypes.TEXT,
      value: values?.value.company_city,
    },
    company_address: {
      type: EFormInputTypes.TEXT,
      value: values?.value.company_address,
    },
    company_postal: {
      type: EFormInputTypes.TEXT,
      value: values?.value.company_postal,
    },
    iban: {
      type: EFormInputTypes.TEXT,
      value: values?.value.iban,
    },
    bic: {
      type: EFormInputTypes.TEXT,
      value: values?.value.bic,
    },
    bank_name: {
      type: EFormInputTypes.TEXT,
      value: values?.value.bank_name,
    },
    bank_country: {
      type: EFormInputTypes.TEXT,
      value: values?.value.bank_country,
    },
    bank_address: {
      type: EFormInputTypes.TEXT,
      value: values?.value.bank_address,
    },
    bank_city: {
      type: EFormInputTypes.TEXT,
      value: values?.value.bank_city,
    },
    swift: {
      type: EFormInputTypes.TEXT,
      value: values?.value.swift,
    },
    phone_number: {
      type: EFormInputTypes.TEXT,
      value: values?.value.phone_number,
    },
  }));

  function isPaymentSystemField(argument: string): argument is IPaymentSystemFieldNames {
    return Object.keys(fullPaymentSystemSchema.value).includes(argument);
  }

  const selectedPaymentSystemSettings = computed(() => (
    PAYMENT_SYSTEMS.find((item) => item.name === paymentSystem.value)
  ));

  const paymentSystemSchema = computed<IPaymentSystemFormSchema>(() => {
    const schema: IPaymentSystemFormSchema = {};
    if (!selectedPaymentSystemSettings.value) {
      return schema;
    }

    for (const [field, options] of Object.entries(selectedPaymentSystemSettings.value.fields)) {
      if (isPaymentSystemField(field)) {
        const allField = fullPaymentSystemSchema.value[field];

        if (allField) {
          Object.assign(schema, {
            [field]: {
              ...allField,
              ...options,
              label: getTranslation(TRANSLATION_GROUP_PAYMENT_SYSTEMS, `${selectedPaymentSystemSettings.value.localizationShortCode}${field}`).value,
            },
          });
        }
      }
    }

    if (withDefault.value) {
      schema.default = {
        type: EFormInputTypes.CHECKBOX,
        label: getTranslation(TRANSLATION_GROUP_PAYMENT_SYSTEMS, 'WITHDRAWAL_DEFAULT').value,
        value: values?.value.default,
        readonly: !!values?.value.default,
        confirm: {
          header: getTranslation(TRANSLATION_GROUP_PAYMENT_SYSTEMS, 'APPROVE_TITLE').value,
          message: getTranslation(TRANSLATION_GROUP_PAYMENT_SYSTEMS, 'APPROVE_QUESTION').value,
          accept: getTranslation(TRANSLATION_GROUP_PAYMENT_SYSTEMS, 'APPROVE_OK').value,
          reject: getTranslation(TRANSLATION_GROUP_PAYMENT_SYSTEMS, 'APPROVE_CANCEL').value,
        },
      };
    }

    return schema;
  });

  return {
    paymentSystemSchema,
    isLoading,
  };
}
